import * as EmotionTheming from 'emotion-theming'
import * as R from 'ramda'
import * as urql from 'urql'
import * as ReactRedux from 'react-redux'
import * as React from 'react'
import * as ReactRouter from 'react-router-dom'
import PropTypes from 'prop-types'
import kebabCase from 'lodash.kebabcase'

import * as common from '@rushplay/common'
import { useI18n } from '@rushplay/i18n'

import * as Configuration from '../configuration'
import * as icons from '../icons'
import { Button } from '../button'
import { DynamicImage } from '../dynamic-image'
import { HtmlContent } from '../html-content'
import { InformativeSection } from '../informative-section'
import { WelcomeOfferGameInfo } from '../welcome-offer-game-info'

import { ManualSignUp } from './manual-signup'

const getFeaturedLandingGames = `
query FeaturedLandingGames {
  catalog(key: "featured-landing-game") {
    ... on GetCatalogSuccess {
      kind
      categories {
        id
        sections {
          id
          key
          layout
          tileSize
          totalGamesCount
          games {
            backgroundUrl
            backgroundOverlayUrl
            category
            colorMode
            displayProvider
            freePlayExcluded
            id
            imageUrl
            key
            preRelease
            provider
            tags
            title
            titleUrl
            jackpotValue
            feature
            rtp
            volatility
            biggestWinCents
            minBetCents
            maxBetCents
            dealerName
            seatsTotal
            seatsAvailable
            betBehind
            releaseDate
          }
        }
      }
    }
    ... on GetCatalogFailure {
      error
      errorCode
    }
  }
}
`

export function LandingPage(props) {
  const briteSigninEnabled = ReactRedux.useSelector(state =>
    Configuration.getBriteSigninEnabled(state.configuration)
  )
  const cdnPrefix = ReactRedux.useSelector(state =>
    Configuration.getCdnPrefix(state.configuration)
  )
  const i18n = useI18n()
  const history = ReactRouter.useHistory()

  const [response] = urql.useQuery({
    query: getFeaturedLandingGames,
  })

  const theme = EmotionTheming.useTheme()

  if (response.fetching) {
    return (
      <common.Box
        display="flex"
        justifyContent="center"
        alignSelf="center"
        fontSize={['48px', '64px']}
        pt={3}
        width="100%"
      >
        <icons.Spinner />
      </common.Box>
    )
  }

  const game = R.path(
    ['data', 'catalog', 'categories', 0, 'sections', 0, 'games', 0],
    response
  )

  return (
    <common.Box display="flex" flexDirection="column" width="100%">
      <common.Box
        backgroundImage={`url(${theme['bg-pattern-default']}), linear-gradient(to bottom, #3c49e4 2%, rgba(106, 57, 189, 0.42) 53%, rgba(23, 6, 29, 0))`}
        backgroundSize={['auto 200%, cover']}
        backgroundPosition="center -300px, center"
      >
        <common.Box
          px={1}
          pt={1}
          display="flex"
          justifyContent="space-around"
          flexDirection={['column', 'row']}
          maxWidth={['500px', '900px']}
          mx="auto"
        >
          <common.Box alignSelf="flex-start" position="relative">
            <DynamicImage
              source={`/${cdnPrefix}/bonus_images/bonus-money.png`}
              alt="Bonus"
              resizingWidth={['200', '250', '350']}
              maxWidth={['200px', '250px', '350px']}
              maxHeight="360px"
              minHeight={['200px', '250px', '350px']}
            />
            <common.Box
              position="absolute"
              top="15%"
              left="0px"
              width="100%"
              textAlign="center"
              fontWeight="bold"
              fontSize={['30px', '40px', '60px']}
            >
              {props.bonusPercentage}%
            </common.Box>
            <common.Box
              position="absolute"
              top="60%"
              left="0px"
              width="100%"
              textAlign="center"
              fontWeight="bold"
              fontSize={['14px', '16px', '20px']}
            >
              {i18n.translate('landing-page.money-bonus.up-to', {
                amountCents: props.maxBonusAmount,
              })}
            </common.Box>
          </common.Box>
          <common.Box
            borderRadius="9999px"
            height="3em"
            width="3em"
            fontSize={[4, 6]}
            backgroundColor="secondary"
            color="g-text"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink="0"
            fontWeight="bold"
            alignSelf="center"
            mx="-1em"
            mt="-4em"
            mb="-2.5em"
            zIndex="1"
          >
            {i18n.translate('landing-page.or')}
          </common.Box>
          <common.Box alignSelf="flex-end" position="relative">
            <DynamicImage
              source={`/${cdnPrefix}/bonus_images/bonus-rounds.png`}
              alt="Jammin Jars"
              resizingWidth={['200', '250', '350']}
              maxWidth={['200px', '250px', '350px']}
              maxHeight="360px"
              minHeight={['200px', '250px', '350px']}
            />
            <common.Box
              position="absolute"
              fontWeight="bold"
              left={['14px', '16px']}
              bottom="17%"
              display="flex"
              flexDirection="column"
            >
              <common.Box fontSize={['18px', '22px', '32px']}>
                {i18n.translate('landing-page.bonus-rounds', {
                  amount: props.maxFeatureTriggers,
                })}
              </common.Box>
              <common.Box fontSize={['14px', '16px', '18px']}>
                {i18n.translate('landing-page.bonus-rounds.game')}
              </common.Box>
            </common.Box>
          </common.Box>
        </common.Box>

        <ManualSignUp />

        {/* Brite signup */}
        {briteSigninEnabled && (
          <common.Box p={2} maxWidth="320px" mx="auto">
            <Button
              stretch
              variant="primary"
              onClick={() => history.push('?login-flow=deposit')}
            >
              <common.Box
                display="flex"
                flexDirection="column"
                fontSize={6}
                justifyContent="center"
                py="4px"
              >
                {i18n.translate('landing-page.register')}
                <common.Box fontSize={1}>
                  {i18n.translate('landing-page.register.sub-header')}
                </common.Box>
              </common.Box>
            </Button>
            <common.Box pt={1} display="flex" justifyContent="center">
              <ReactRouter.Link to="?login=me">
                <common.Box
                  p={0}
                  fontSize={2}
                  style={{ textDecoration: 'underline' }}
                >
                  {i18n.translate('landing-page.login.cta')}
                </common.Box>
              </ReactRouter.Link>
            </common.Box>
          </common.Box>
        )}
        <common.Box backgroundImage="linear-gradient(180deg, rgba(23, 6, 29, 0.01) 0%, rgba(23, 6, 29, 0.9) 40%, rgb(23, 6, 29) 100%)">
          <common.Box
            pt={{ md: '80px' }}
            maxWidth="1000px"
            mx="auto"
            minHeight="480px"
          >
            {game && (
              <WelcomeOfferGameInfo
                gameId={game.id}
                backgroundUrl={game.backgroundUrl}
                description={`game-info.${kebabCase(
                  game.displayProvider || game.provider
                )}.${kebabCase(game.key)}.description`}
                gameKey={game.key}
                provider={game.provider}
                title={game.title}
                titleUrl={game.titleUrl}
                jackpotValue={game.jackpotValue}
                feature={game.feature}
                rtp={game.rtp}
                volatility={game.volatility}
                biggestWinCents={game.biggestWinCents}
                minBetCents={game.minBetCents}
                maxBetCents={game.maxBetCents}
                dealerName={game.dealerName}
                seatsTotal={game.seatsTotal}
                seatsAvailable={game.seatsAvailable}
                betBehind={game.betBehind}
                releaseDate={game.releaseDate}
              />
            )}
          </common.Box>
        </common.Box>
      </common.Box>
      <common.Box maxWidth="1080px" alignSelf="center" py={[2, 6]}>
        <InformativeSection />
      </common.Box>
      <common.Box
        p={2}
        maxWidth="1080px"
        mx="auto"
        textAlign="left"
        fontSize={2}
      >
        <HtmlContent
          html={{
            __html: i18n.translate('landing-page.seo-content'),
          }}
        />
      </common.Box>
    </common.Box>
  )
}

LandingPage.propTypes = {
  bonusPercentage: PropTypes.number.isRequired,
  maxBonusAmount: PropTypes.number.isRequired,
  maxFeatureTriggers: PropTypes.number.isRequired,
}

// for @loadable/components
export default LandingPage
